import React from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import gfm from "remark-gfm";
import { Globe } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import VerticalSpace from "../components/VerticalSpace";

import PageTitle from "../components/PageTitle";
import withScrollToTopWhenMounted from "../components/withScrollToTopWhenMounted";
import { aboutEnglishMd, aboutDanishMd } from "../markdown/about";
import theme from "../theme";

const LanguageSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;

  @media (max-width: 650px) {
    justify-content: center;
    margin-bottom: 25px;
  }
`;

const LanguageLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
`;

const LanguageButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const LanguageButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${(props) => (props.$active ? props.theme.primary : "#ddd")};
  border-radius: 4px;
  background: ${(props) => (props.$active ? props.theme.primary : "#fff")};
  color: ${(props) => (props.$active ? "#fff" : "#666")};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => props.theme.primary};
    color: ${(props) => (props.$active ? "#fff" : props.theme.primary)};
  }
`;

const MarkdownContainer = styled.div`
  max-width: 600px;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    display: block;
    color: #444;
    font-weight: bold;
    text-transform: uppercase;
    font-family: "Gaegu", sans-serif;
    margin: 0;
  }

  h1 {
    font-size: ${theme.fontSizes.giant};
    @media (max-width: 650px) {
      font-size: ${theme.fontSizes.xhuge};
    }
  }
  h2 {
    font-size: ${theme.fontSizes.xhuge};
    @media (max-width: 650px) {
      font-size: ${theme.fontSizes.huge};
    }
  }
  h3 {
    font-size: ${theme.fontSizes.huge};
    @media (max-width: 650px) {
      font-size: ${theme.fontSizes.xlarge};
    }
  }
  h4 {
    font-size: ${theme.fontSizes.xlarge};
    @media (max-width: 650px) {
      font-size: ${theme.fontSizes.large};
    }
  }
  h5 {
    font-size: ${theme.fontSizes.large};
    @media (max-width: 650px) {
      font-size: ${theme.fontSizes.medium};
    }
  }
  h6 {
    font-size: ${theme.fontSizes.medium};
    @media (max-width: 650px) {
      font-size: ${theme.fontSizes.small};
    }
  }

  p {
    line-height: 1.5;
    font-size: ${theme.fontSizes.medium};
  }

  hr {
    border: none;
    border-top: 5px solid #bbb;
    margin: 100px 0;
  }
`;

const AboutView = () => {
  const { lang } = useParams();
  const navigate = useNavigate();

  // Determine current language - default to English if no language or invalid language
  const currentLanguage = lang === "da" || lang === "dk" ? "danish" : "english";

  const handleLanguageChange = (language) => {
    const langCode = language === "danish" ? "da" : "en";
    navigate(`/about/${langCode}`);
  };

  return (
    <React.Fragment>
      <MarkdownContainer>
        <PageTitle>About</PageTitle>
        <VerticalSpace $amount={20} />
        <LanguageSelector>
          <LanguageLabel>
            <Globe size={14} />
            Language:
          </LanguageLabel>
          <LanguageButtons>
            <LanguageButton
              $active={currentLanguage === "english"}
              onClick={() => handleLanguageChange("english")}
            >
              English
            </LanguageButton>
            <LanguageButton
              $active={currentLanguage === "danish"}
              onClick={() => handleLanguageChange("danish")}
            >
              Dansk
            </LanguageButton>
          </LanguageButtons>
        </LanguageSelector>

        <ReactMarkdown remarkPlugins={[gfm]} rehypePlugins={[rehypeRaw]}>
          {currentLanguage === "english" ? aboutEnglishMd : aboutDanishMd}
        </ReactMarkdown>
      </MarkdownContainer>
    </React.Fragment>
  );
};

export default withScrollToTopWhenMounted(AboutView);
